import React, { useState, useEffect, useMemo } from 'react';
import {
  Container,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  Chip,
} from '@mui/material';
import { FileDownloadOutlined as DownloadIcon, Search as SearchIcon } from '@mui/icons-material';
//Constants
import { eventUrl, reportUrls } from '../../../constants';
import { useAuthContext } from '../../../context/auth-context';
import AffiliationFilter from '../../../components/reports/AffiliationFilter';
import { getAffiliationZones, filterByAffiliationZone } from '../../../utils/report-filters';
import { authFetch } from '../../../lib/http';

const EventInspectionsPage = () => {
  const { activeCamp } = useAuthContext();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [events, setEvents] = useState([]);
  const [selectedEvents, setSelectedEvents] = useState([]);
  const [zoneFilter, setZoneFilter] = useState('');

  useEffect(() => {
    if (!activeCamp?._id) {
      setEvents([]);
      setSelectedEvents([]);
      return;
    }
    fetchEvents(activeCamp._id);
  }, [activeCamp?._id]);

  const fetchEvents = async (campId) => {
    try {
      const response = await authFetch(eventUrl.GET_INSPECTION_EVENTS + campId);
      const result = await response.json();
      setEvents(result);
    } catch (error) {
      console.error('Error fetching events:', error);
    }
  };

  const fetchData = async () => {
    if (selectedEvents.length === 0) return;

    setLoading(true);
    try {
      const response = await authFetch(reportUrls.EVENTS, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ events: selectedEvents }),
      });

      const result = await response.json();

      // Group by club when multiple events selected
      const grouped = {};
      (result.results || []).forEach((row) => {
        const key = row.clubName;
        if (!grouped[key]) {
          grouped[key] = {
            clubName: row.clubName,
            affiliationName: row.affiliationName,
            affiliationZone: row.affiliationZone,
            eventName: selectedEvents.length > 1 ? 'Multiple Events' : row.eventName,
            sumGrade: 0,
            maxPointEvents: 0,
          };
        }
        grouped[key].sumGrade += row.sumGrade || 0;
        grouped[key].maxPointEvents += row.maxPointEvents || 0;
      });

      const groupedData = Object.values(grouped);

      // Sort by percentage descending
      const sortedData = groupedData.sort((a, b) => {
        const percentageA = calculatePercentage(a.sumGrade, a.maxPointEvents);
        const percentageB = calculatePercentage(b.sumGrade, b.maxPointEvents);
        return parseFloat(percentageB) - parseFloat(percentageA);
      });

      setData(sortedData);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getBanderinColor = (percentage) => {
    if (percentage >= 95) return '#2196F3'; // Azul
    if (percentage >= 85) return '#F44336'; // Rojo
    return '#16B819'; // Verde para < 85%
  };

  const getBanderinText = (percentage) => {
    if (percentage >= 95) return 'Azul';
    if (percentage >= 85) return 'Rojo';
    return 'Verde';
  };

  const calculatePercentage = (sumGrade, maxPoints) => {
    if (!maxPoints || maxPoints === 0) return 0;
    return ((sumGrade / maxPoints) * 100).toFixed(2);
  };

  const zones = useMemo(() => getAffiliationZones(data), [data]);
  const filteredData = useMemo(
    () => filterByAffiliationZone(data, zoneFilter),
    [data, zoneFilter]
  );

  const colorCounts = useMemo(() => {
    const counts = {
      Azul: 0,
      Rojo: 0,
      Verde: 0,
    };

    filteredData.forEach((row) => {
      const percentage = calculatePercentage(row.sumGrade, row.maxPointEvents);
      const banderin = getBanderinText(parseFloat(percentage));
      counts[banderin]++;
    });

    return counts;
  }, [filteredData]);

  const handleExport = () => {
    const headers = ['Event', 'Club', 'Misión/Asociación', 'Grade', 'Max Grade', 'Porcentaje (%)', 'Banderín'];
    const csvContent = [
      headers.join(','),
      ...filteredData.map((row) => {
        const percentage = calculatePercentage(row.sumGrade, row.maxPointEvents);
        const banderin = getBanderinText(parseFloat(percentage));
        return [
          `"${row.eventName}"`,
          `"${row.clubName}"`,
          `"${row.affiliationName}"`,
          `"${row.sumGrade}"`,
          `"${row.maxPointEvents || 0}"`,
          `"${percentage}"`,
          `"${banderin}"`,
        ].join(',');
      }),
    ].join('\n');

    const bom = '\uFEFF';
    const blob = new Blob([bom + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'evento-inscripcion.csv';
    link.click();
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" mb={3}>
        Evento Inscripción
      </Typography>

      <Box display="flex" gap={2} mb={3} alignItems="flex-start" flexWrap="wrap">
        <FormControl sx={{ minWidth: 300 }}>
          <InputLabel>Seleccionar Eventos</InputLabel>
          <Select
            multiple
            value={selectedEvents}
            onChange={(e) => setSelectedEvents(e.target.value)}
            input={<OutlinedInput label="Seleccionar Eventos" />}
            renderValue={(selected) => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {selected.map((value) => (
                  <Chip
                    key={value}
                    label={events.find((e) => e._id === value)?.name || value}
                    size="small"
                  />
                ))}
              </Box>
            )}
          >
            {events.map((event) => (
              <MenuItem key={event._id} value={event._id}>
                {event.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Button
          variant="contained"
          startIcon={<SearchIcon />}
          onClick={fetchData}
          disabled={selectedEvents.length === 0}
        >
          Buscar
        </Button>

        <Box sx={{ minWidth: 240 }}>
          <AffiliationFilter value={zoneFilter} onChange={setZoneFilter} zones={zones} />
        </Box>

        <Button
          variant="contained"
          color="success"
          startIcon={<DownloadIcon />}
          onClick={handleExport}
          disabled={filteredData.length === 0}
        >
          Exportar
        </Button>
      </Box>

      <Box
        display="flex"
        gap={2}
        mb={2}
        justifyContent="flex-end"
        flexWrap="wrap"
      >
        <Chip
          label={`Azul (${colorCounts.Azul})`}
          sx={{
            backgroundColor: '#2196F3',
            color: 'white',
            fontWeight: 'bold',
          }}
        />
        <Chip
          label={`Rojo (${colorCounts.Rojo})`}
          sx={{
            backgroundColor: '#F44336',
            color: 'white',
            fontWeight: 'bold',
          }}
        />
        <Chip
          label={`Verde (${colorCounts.Verde})`}
          sx={{
            backgroundColor: '#16B819',
            color: 'white',
            fontWeight: 'bold',
          }}
        />
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" mt={4}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell><strong>Event</strong></TableCell>
                <TableCell><strong>Club</strong></TableCell>
                <TableCell><strong>Misión/Asociación</strong></TableCell>
                <TableCell><strong>Grade</strong></TableCell>
                <TableCell><strong>Max Grade</strong></TableCell>
                <TableCell><strong>Porcentaje (%)</strong></TableCell>
                <TableCell><strong>Banderín</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredData.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    {selectedEvents.length === 0
                      ? 'Selecciona eventos y presiona Buscar'
                      : 'No hay datos para mostrar'}
                  </TableCell>
                </TableRow>
              ) : (
                filteredData.map((row, index) => {
                  const percentage = calculatePercentage(row.sumGrade, row.maxPointEvents);
                  const banderinColor = getBanderinColor(parseFloat(percentage));
                  const banderinText = getBanderinText(parseFloat(percentage));

                  return (
                    <TableRow key={index} hover>
                      <TableCell>{row.eventName}</TableCell>
                      <TableCell>{row.clubName}</TableCell>
                      <TableCell>{row.affiliationName}</TableCell>
                      <TableCell>{row.sumGrade}</TableCell>
                      <TableCell>{row.maxPointEvents || 0}</TableCell>
                      <TableCell>{percentage}%</TableCell>
                      <TableCell
                        sx={{
                          backgroundColor: banderinColor,
                          color: 'white',
                          fontWeight: 'bold',
                        }}
                      >
                        {banderinText}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Container>
  );
};

export default EventInspectionsPage;